import React, { useEffect, useRef, useState } from "react";

interface FilterRotatorProps {
  items?: string[];
  interval?: number;
  duration?: number;
  style?: React.CSSProperties;
  labelClassName?: string;
  hasSelected?: boolean;
  showColors?: boolean
}

export default function FilterRotator({
  items = ["More Filters"],
  interval = 3000,
  duration = 500,
  style = {},
  labelClassName = "",
  hasSelected = false,
  showColors
}: FilterRotatorProps) {
  const [index, setIndex] = useState(0);
  const [anim, setAnim] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const textColors = ["#B96D16", "#118EEB", "#cda501", "#029847", "#7b087b"];
  const bgColors = ["#FFF5E9", "#EDF7FF", "#fffae2", "#EAFFF4", "#e5dbfdff"];

  useEffect(() => {
    if (!items || items.length <= 1) return;

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    intervalRef.current = window.setInterval(() => {
      setAnim(true);
    }, interval);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [interval, items]);

  useEffect(() => {
    if (!anim) return;

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;

      scroller.style.transition = "none";
      scroller.style.transform = "translateY(0)";

      setIndex((i) => (i + 1) % items.length);
      setAnim(false);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scroller) {
            scroller.style.transition = `transform ${duration}ms ease`;
          }
        });
      });

      timeoutRef.current = null;
    }, duration);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [anim, duration, items]);

  if (!items || items.length === 0) return null;

  const getCurrentTextColor = () => {
    if (!showColors) {
      return hasSelected ? "#00A392" : ""
    }
    return textColors[index % textColors.length];
  };

  const getNextTextColor = () => {
    if (!showColors) {
      return hasSelected ? "#00A392" : ""
    }
    return textColors[(index + 1) % textColors.length];
  };

  const getCurrentBgColor = () => {
    if (!showColors) return ""
    return bgColors[index % bgColors.length];
  };

  const getNextBgColor = () => {
    if (!showColors) return ""
    return bgColors[(index + 1) % bgColors.length];
  };

  if (items.length === 1) {
    return (
      <span
        className={labelClassName}
        style={{
          color: getCurrentTextColor(),
          fontSize: style.fontSize ?? "1em",
          fontWeight: style.fontWeight ?? 500,
          // background: `linear-gradient(to right, ${getCurrentBgColor()}, transparent)`,
          // borderRadius: "20px",
          display: "inline-block",
        }}
      >
        {items[0]}
      </span>
    );
  }

  return (
    <div
      style={{
        display: "inline-block",
        verticalAlign: "middle",
        position: "relative",
        width: "5.1em",
        maxWidth: "5.1em",
        minWidth: "5.1em",
        height: "2em",
        // borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      {/* Background layer with gradient */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // background: `linear-gradient(to right, ${anim ? getNextBgColor() : getCurrentBgColor()}, transparent)`,
          transition: `background ${duration}ms ease`,
          // borderRadius: "20px",
          zIndex: 0,
        }}
      />

      {/* Text content layer */}
      <div
        ref={scrollerRef}
        style={{
          position: "relative",
          zIndex: 1,
          transition: `transform ${duration}ms ease`,
          transform: anim ? "translateY(-50%)" : "translateY(0)",
          willChange: "transform",
        }}
        aria-live="polite"
      >
        <span
          className={labelClassName}
          style={{
            display: "block",
            color: anim ? getNextTextColor() : getCurrentTextColor(),
            fontSize: style.fontSize ?? "1em",
            fontWeight: style.fontWeight ?? 500,
            lineHeight: "2em",
            whiteSpace: "nowrap",
            transition: `color ${duration}ms ease`,
            width: "5.1em",
            minWidth: "5.1em",
            maxWidth: "5.1em",
          }}
        >
          {items[index]}
        </span>

        <span
          className={labelClassName}
          style={{
            display: "block",
            color: anim ? getNextTextColor() : getCurrentTextColor(),
            fontSize: style.fontSize ?? "1em",
            fontWeight: style.fontWeight ?? 500,
            lineHeight: "2em",
            whiteSpace: "nowrap",
            transition: `color ${duration}ms ease`,
          }}
        >
          {items[(index + 1) % items.length]}
        </span>
      </div>
    </div>
  );
}
